import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Launch } from '@carbon/react/icons';
import type { PartyLink } from '../../types';
import { useCardGet } from './use-card-resource';
import { DASH, ExpandState, ExpandTable, PartyCell } from './common';
import { format8 } from 'utils/number';
import { parseMomentUtc } from 'utils/dates';
import { occurrenceKeys } from 'utils/occurrence-keys';
import styles from './expand.module.scss';

interface UserRef {
  email?: string | null;
}

// GET /documents/:id/transactions — one row per interpretation transaction ×
// grantor × grantee, carrying the bundle of sticks. Keys arrive camelCased
// (global jbuilder key_format). Rendered inside the Transaction Table accordion,
// so no section header here. Created/Updated By & At are interpretation-level
// (same source as the Interpretations section); per-party book/page stays out.
interface TransactionApiRow {
  id: number | string;
  interpretationId: number | string;
  interpretationHref?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: UserRef | null;
  lastSavedBy?: UserRef | null;
  sequence?: number | null;
  surveyAreas?: string[] | null;
  grantor?: PartyLink | null;
  grantee?: PartyLink | null;
  conveyanceType?: string | null;
  ofType?: string | null;
  mineral?: number | string | null;
  surface?: number | string | null;
  executive?: number | string | null;
  bonus?: number | string | null;
  delay?: number | string | null;
  working?: number | string | null;
  royalty?: number | string | null;
  orri?: number | string | null;
  fixedRoyalty?: number | string | null;
}

// The bundle of sticks: nine interest columns, right-aligned, shown as the raw
// multiplier the interpretation recorded. Royalty and Fixed Royalty are the same
// royalty conveyance split by its `fixed` flag (resolved on the backend).
const STICKS: { header: string; key: keyof TransactionApiRow }[] = [
  { header: 'Mineral', key: 'mineral' },
  { header: 'Surface', key: 'surface' },
  { header: 'Executive', key: 'executive' },
  { header: 'Bonus', key: 'bonus' },
  { header: 'Delay', key: 'delay' },
  { header: 'Working Interest', key: 'working' },
  { header: 'Royalty', key: 'royalty' },
  { header: 'ORRI', key: 'orri' },
  { header: 'Fixed Royalty', key: 'fixedRoyalty' },
];

const PAGE_SIZE = 25;

const NO_PARTY: PartyLink = { name: null, entityId: null };

/**
 * A value, or an em dash when blank.
 * @param {number | string | null} value The value.
 * @return {number | string} What the cell shows.
 */
const dash = (value?: number | string | null) => {
  return value === null || value === undefined || value === '' ? DASH : value;
};

/**
 * A timestamp as a date and time, or blank.
 * @param {string | null} value The timestamp.
 * @return {string} What the cell shows.
 */
const fmtDateTime = (value?: string | null) => {
  return value ? parseMomentUtc(value).format('MM/DD/YYYY, hh:mm A') : '';
};

/**
 * Bundle-of-sticks values render to 8 decimal places (narrative text + parity
 * with the Ownerships table); blank stays an em dash and a non-numeric value
 * (e.g. an already-formatted percent) passes through unchanged.
 * @param {number | string | null} value The stick.
 * @return {number | string} What the cell shows.
 */
const formatStick = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return DASH;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? format8(n) : value;
};

interface TransactionsExpandProps {
  documentId: number | string;
  // Opens the per-interpretation tear sheet for a transaction row (narrative:
  // "every interpretation row has its own launch icon"). The card resolves the
  // interpretation by id and drives its shared drawer.
  onOpenInterp?: (interpretationId: number | string) => void;
}

/**
 * The document's transactions: one row per interpretation conveyance.
 * @param {TransactionsExpandProps} props The expand.
 * @return {JSX.Element} The transactions.
 */
export function TransactionsExpand({
  documentId,
  onOpenInterp,
}: TransactionsExpandProps) {
  const { loading, data, error } = useCardGet<TransactionApiRow[]>(
    `/documents/${documentId}/transactions`
  );
  const [expandedAreas, setExpandedAreas] = useState<Set<number | string>>(
    new Set()
  );

  if (loading) return <ExpandState>Loading…</ExpandState>;
  if (error) return <ExpandState>Failed to load transactions.</ExpandState>;

  const rows = data ?? [];
  if (rows.length === 0)
    return (
      <ExpandState>
        No transaction data available for this document.
      </ExpandState>
    );

  /**
   * Shows or hides a row's extra survey areas.
   * @param {number | string} id The row.
   */
  const toggleAreas = (id: number | string) =>
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ExpandTable<TransactionApiRow>
      columns={[
        {
          header: 'Interpretation #',
          cell: (row) => (
            <span className={styles.interpCell}>
              {row.interpretationHref ? (
                <NavLink
                  to={row.interpretationHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.interpretationId}
                </NavLink>
              ) : (
                row.interpretationId
              )}
              {onOpenInterp && (
                <button
                  type="button"
                  className={styles.interpLaunch}
                  aria-label="Open interpretation"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenInterp(row.interpretationId);
                  }}
                >
                  <Launch size={14} />
                </button>
              )}
            </span>
          ),
        },
        { header: 'Sequence', cell: (row) => dash(row.sequence) },
        {
          header: 'Survey Areas',
          cell: (row) => {
            const areas = row.surveyAreas ?? [];
            if (areas.length === 0)
              return <span className={styles.muted}>{DASH}</span>;
            const showAll = expandedAreas.has(row.id);
            const visible = showAll ? areas : areas.slice(0, 1);
            const keys = occurrenceKeys(visible);
            const extra = areas.length - 1;
            return (
              <>
                {visible.map((area, i) => (
                  <div key={keys[i]}>{area}</div>
                ))}
                {extra > 0 && (
                  <button
                    type="button"
                    className={styles.areaToggle}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAreas(row.id);
                    }}
                  >
                    {showAll
                      ? 'View less'
                      : `View more survey areas (+${extra})`}
                  </button>
                )}
              </>
            );
          },
        },
        {
          header: 'Conveyor',
          cell: (row) => <PartyCell party={row.grantor ?? NO_PARTY} />,
        },
        {
          header: 'Conveyee',
          cell: (row) => <PartyCell party={row.grantee ?? NO_PARTY} />,
        },
        { header: 'Conveyance Type', cell: (row) => dash(row.conveyanceType) },
        { header: 'Of Type', cell: (row) => dash(row.ofType) },
        ...STICKS.map((stick) => ({
          header: stick.header,
          align: 'right' as const,
          cell: (row: TransactionApiRow) =>
            formatStick(row[stick.key] as number | string | null | undefined),
        })),
        { header: 'Created By', cell: (row) => dash(row.createdBy?.email) },
        {
          header: 'Created At',
          cell: (row) => dash(fmtDateTime(row.createdAt)),
        },
        { header: 'Updated By', cell: (row) => dash(row.lastSavedBy?.email) },
        {
          header: 'Updated At',
          cell: (row) => dash(fmtDateTime(row.updatedAt)),
        },
      ]}
      rows={rows}
      rowKey={(row) => row.id}
      pageSize={PAGE_SIZE}
    />
  );
}
